/**
 * Visual pieces for the private meeting setup ("green room") screen.
 *
 * Kept apart from ModernMeetingSetup so the animated parts re-render on their
 * own: the mic meter updates ~15 times a second without touching the large
 * setup component. All motion is transform/opacity and stops under
 * prefers-reduced-motion.
 */
import React, { useEffect, useRef, useState } from "react";
import { MEDIASFU_BRAND } from "./brandPalette";

const STYLE_ID = "mms-visuals-style";

const css = `
.mms-aurora { position: fixed; inset: 0; pointer-events: none; overflow: hidden; z-index: 0; }
.mms-aurora__blob { position: absolute; border-radius: 50%; filter: blur(60px); will-change: transform; }
.mms-aurora__blob--a { width: 720px; height: 520px; right: -180px; top: -220px; animation: mms-drift-a 38s ease-in-out infinite alternate; }
.mms-aurora__blob--b { width: 620px; height: 460px; left: -220px; bottom: -260px; animation: mms-drift-b 46s ease-in-out infinite alternate; }
.mms-aurora__grid { position: absolute; inset: 0;
  background-image: linear-gradient(rgba(148,163,184,0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.055) 1px, transparent 1px);
  background-size: 44px 44px;
  -webkit-mask-image: radial-gradient(ellipse 70% 60% at 60% 30%, #000 0%, transparent 75%);
  mask-image: radial-gradient(ellipse 70% 60% at 60% 30%, #000 0%, transparent 75%); }
@keyframes mms-drift-a { from { transform: translate3d(0,0,0) scale(1); } to { transform: translate3d(-120px,80px,0) scale(1.12); } }
@keyframes mms-drift-b { from { transform: translate3d(0,0,0) scale(1); } to { transform: translate3d(140px,-60px,0) scale(1.08); } }

.mms-orbit { position: relative; width: 100%; height: 100%; display: grid; place-items: center; overflow: hidden; }
.mms-orbit__ring { position: absolute; border-radius: 50%; border: 1px solid rgba(148,163,184,0.16); }
.mms-orbit__ring--1 { width: 120px; height: 120px; }
.mms-orbit__ring--2 { width: 200px; height: 200px; border-color: rgba(148,163,184,0.11); }
.mms-orbit__ring--3 { width: 290px; height: 290px; border-color: rgba(148,163,184,0.07); }
.mms-orbit__spin { position: absolute; width: 200px; height: 200px; animation: mms-spin 24s linear infinite; }
.mms-orbit__spin--slow { width: 290px; height: 290px; animation-duration: 40s; animation-direction: reverse; }
.mms-orbit__node { position: absolute; top: -4px; left: calc(50% - 4px); width: 8px; height: 8px; border-radius: 50%; }
.mms-orbit__core { position: relative; width: 84px; height: 84px; border-radius: 50%; display: grid; place-items: center;
  font: 700 30px/1 'Space Grotesk', 'Inter', system-ui, sans-serif; letter-spacing: -0.02em; }
@keyframes mms-spin { to { transform: rotate(360deg); } }

.mms-meter { display: inline-flex; align-items: flex-end; gap: 3px; height: 16px; }
.mms-meter__bar { width: 3px; border-radius: 3px; height: 100%; transform-origin: bottom; transition: transform 90ms linear; }

.mms-live-ring { position: absolute; inset: -1px; border-radius: inherit; padding: 1.5px; pointer-events: none;
  background: conic-gradient(from var(--mms-angle, 0deg), rgba(45,212,191,0.0), rgba(45,212,191,0.9), rgba(56,189,248,0.9), rgba(56,189,248,0.0) 40%, rgba(45,212,191,0.0));
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor; mask-composite: exclude;
  /* Two sweeps when the camera goes live, then a quiet static edge: a perpetual
     sweep repainted the gradient every frame beside a live video preview. */
  animation: mms-ring 2.4s cubic-bezier(0.45, 0, 0.25, 1) 2, mms-ring-settle 0.8s ease 4.8s forwards; }
@property --mms-angle { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
@keyframes mms-ring { to { --mms-angle: 360deg; } }
@keyframes mms-ring-settle { to { opacity: 0.4; } }

/* One entrance (form first, preview a beat after) and a short crossfade when
   switching Join / Start. Motion that orients, nothing that loops. */
@keyframes mms-rise { from { opacity: 0; transform: translate3d(0, 10px, 0); } to { opacity: 1; transform: none; } }
@keyframes mms-swap { from { opacity: 0; transform: translate3d(0, 6px, 0); } to { opacity: 1; transform: none; } }
.modern-meeting-setup .mms-panel { animation: mms-rise 0.5s cubic-bezier(0.2, 0.7, 0.2, 1) both; }
.modern-meeting-setup .mms-stage { animation: mms-rise 0.5s cubic-bezier(0.2, 0.7, 0.2, 1) 0.08s both; }
.mms-swap { animation: mms-swap 0.32s cubic-bezier(0.2, 0.7, 0.2, 1) both; }

@media (prefers-reduced-motion: reduce) {
  .mms-aurora__blob, .mms-orbit__spin, .mms-live-ring,
  .modern-meeting-setup .mms-panel, .modern-meeting-setup .mms-stage, .mms-swap { animation: none !important; }
  .mms-meter__bar { transition: none; }
}
`;

/** Injects the shared keyframes once per document. */
export const SetupVisualStyles: React.FC = () => {
  useEffect(() => {
    if (typeof document === "undefined" || document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = css;
    document.head.appendChild(el);
  }, []);
  return null;
};

/** Two slow teal/sky glows and a faint signal grid behind the whole screen. */
export const AuroraBackdrop: React.FC<{ isDarkMode: boolean }> = ({ isDarkMode }) => (
  <div className="mms-aurora" aria-hidden="true">
    <div
      className="mms-aurora__blob mms-aurora__blob--a"
      style={{ background: `rgba(20,184,166,${isDarkMode ? 0.22 : 0.16})` }}
    />
    <div
      className="mms-aurora__blob mms-aurora__blob--b"
      style={{ background: `rgba(56,189,248,${isDarkMode ? 0.12 : 0.12})` }}
    />
    <div className="mms-aurora__grid" style={{ opacity: isDarkMode ? 1 : 0.7 }} />
  </div>
);

/** Camera-off state: the person's initial in a calm orbit of participants. */
export const OrbitPlaceholder: React.FC<{ initial: string; isDarkMode: boolean }> = ({
  initial,
  isDarkMode,
}) => (
  <div className="mms-orbit" aria-hidden="true">
    <div className="mms-orbit__ring mms-orbit__ring--1" />
    <div className="mms-orbit__ring mms-orbit__ring--2" />
    <div className="mms-orbit__ring mms-orbit__ring--3" />
    <div className="mms-orbit__spin">
      <span className="mms-orbit__node" style={{ background: MEDIASFU_BRAND.teal }} />
    </div>
    <div className="mms-orbit__spin mms-orbit__spin--slow">
      <span className="mms-orbit__node" style={{ background: MEDIASFU_BRAND.sky, opacity: 0.8 }} />
    </div>
    <div
      className="mms-orbit__core"
      style={{
        background: MEDIASFU_BRAND.gradient,
        color: MEDIASFU_BRAND.ink,
        boxShadow: `0 0 0 8px ${isDarkMode ? "rgba(45,212,191,0.08)" : "rgba(20,184,166,0.10)"}`,
      }}
    >
      {initial}
    </div>
  </div>
);

/**
 * Live microphone level from the preview stream. Reads the stream from a ref
 * (the setup screen swaps it when devices change) and re-attaches whenever the
 * stream identity changes. Analysis only; nothing is played back.
 */
export const MicLevelMeter: React.FC<{
  streamRef: React.MutableRefObject<MediaStream | null>;
  active: boolean;
  color?: string;
  idleColor?: string;
}> = ({ streamRef, active, color = MEDIASFU_BRAND.teal, idleColor = "rgba(148,163,184,0.35)" }) => {
  const [level, setLevel] = useState(0);
  const ctxRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    if (!active) {
      setLevel(0);
      return;
    }
    let raf = 0;
    let last = 0;
    let attached: MediaStream | null = null;
    let analyser: AnalyserNode | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    const data = new Uint8Array(256);

    const attach = () => {
      const stream = streamRef.current;
      if (stream === attached) return;
      source?.disconnect();
      source = null;
      analyser = null;
      attached = stream;
      if (!stream || stream.getAudioTracks().length === 0) return;
      try {
        const Ctx = window.AudioContext || (window as any).webkitAudioContext;
        if (!Ctx) return;
        ctxRef.current = ctxRef.current ?? new Ctx();
        analyser = ctxRef.current.createAnalyser();
        analyser.fftSize = 512;
        source = ctxRef.current.createMediaStreamSource(stream);
        source.connect(analyser);
      } catch {
        analyser = null;
      }
    };

    const tick = (t: number) => {
      raf = requestAnimationFrame(tick);
      if (t - last < 66) return; // ~15fps is plenty for a meter
      last = t;
      attach();
      if (!analyser) return;
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      setLevel((prev) => prev * 0.55 + Math.min(1, rms * 4) * 0.45);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      source?.disconnect();
    };
  }, [active, streamRef]);

  useEffect(
    () => () => {
      ctxRef.current?.close().catch(() => undefined);
      ctxRef.current = null;
    },
    [],
  );

  const bars = [0.45, 0.75, 1, 0.75, 0.45];
  return (
    <span className="mms-meter" role="meter" aria-label="Microphone level" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(level * 100)}>
      {bars.map((weight, i) => {
        const h = Math.max(0.18, Math.min(1, level * weight * 1.6));
        return (
          <span
            key={i}
            className="mms-meter__bar"
            style={{ transform: `scaleY(${h})`, background: level > 0.04 ? color : idleColor }}
          />
        );
      })}
    </span>
  );
};
